import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { Separator } from "@/components/ui/separator";
import { apiClient, auth, formatMoney } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium">{value}</span>
  </div>
);

export default function PartnerReferralView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (auth.getRole() !== "partner") {
      navigate("/partner/login");
      return;
    }
    apiClient
      .get(`/referrals/${id}`)
      .then((r) => setReferral(r.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Could not load referral");
        if (err?.response?.status === 401) navigate("/partner/login");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <DashboardShell role="partner" title="Referral">
        <div className="p-12 text-center text-slate-500">Loading…</div>
      </DashboardShell>
    );
  }
  if (!referral) return null;

  const commissionDisplay = referral.commission_amount
    ? formatMoney(referral.commission_amount)
    : `${formatMoney(referral.estimated_commission || 0)} est.`;

  return (
    <DashboardShell
      role="partner"
      title={referral.business_name}
      subtitle={`Submitted ${new Date(referral.created_at).toLocaleDateString()}`}
      actions={
        <Link to="/partner/dashboard">
          <Button variant="outline" className="rounded-full">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </Link>
      }
    >
      <div data-testid={TIDS.referralDetail} className="grid lg:grid-cols-3 gap-6 max-w-4xl">
        {/* Status & commission summary */}
        <Card className="rounded-xl border-slate-200 shadow-sm p-6 lg:col-span-2 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Current status</div>
              <div className="mt-2"><StatusPill status={referral.status} /></div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Commission</div>
              <div className="mt-1 text-2xl font-heading font-medium text-slate-900">
                {commissionDisplay}
              </div>
              {referral.commission_paid ? (
                <span className="text-xs text-emerald-700 font-medium">paid in full</span>
              ) : (
                <span className="text-xs text-slate-500">paid on funding</span>
              )}
            </div>
          </div>

          {referral.partner_notes ? (
            <>
              <Separator />
              <div>
                <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-2">
                  Note from the team
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {referral.partner_notes}
                </p>
              </div>
            </>
          ) : null}

          <Separator />
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-200 text-slate-600 shrink-0">
              <Lock className="h-4 w-4" />
            </span>
            <div className="text-sm text-slate-600 leading-relaxed">
              <span className="font-medium text-slate-900">Business details are kept private.</span>{" "}
              Our team is handling underwriting, documentation, and lender placement on your behalf. You&apos;ll see status updates here and the commission settles automatically when the deal funds.
            </div>
          </div>
        </Card>

        {/* Side panel */}
        <Card className="rounded-xl border-slate-200 shadow-sm p-6 space-y-3 h-fit">
          <h2 className="text-lg font-medium text-slate-900">Summary</h2>
          <SummaryRow label="Business" value={referral.business_name} />
          <SummaryRow label="Submitted" value={new Date(referral.created_at).toLocaleDateString()} />
          {referral.updated_at && referral.updated_at !== referral.created_at ? (
            <SummaryRow label="Last update" value={new Date(referral.updated_at).toLocaleDateString()} />
          ) : null}
          <SummaryRow
            label="Commission"
            value={referral.commission_paid
              ? <span className="text-emerald-700">Paid</span>
              : referral.commission_amount
              ? "Pending payout"
              : "Estimated"}
          />
        </Card>
      </div>
    </DashboardShell>
  );
}
